import { Button, Input } from '@artosera/ui';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import {
  useMettreAJourRedirections,
  type CorpsRedirection,
  type RedirectionSite,
} from '../../../api/site.js';
import { galerieALaction } from '../../../auth/sujet.js';
import type { GalerieSession } from '../../../api/types.js';
import styles from './Tabs.module.css';

export interface RedirectionsTabProps {
  galerieSlug: string;
  redirections: readonly RedirectionSite[];
  galerie: GalerieSession;
}

/**
 * Redirections 301 (S6-f, § 15.7) : table ancien → nouveau. `PUT` remplace la
 * table entière — un « Enregistrer » unique, plutôt qu'un appel par ligne.
 */
export function RedirectionsTab({
  galerieSlug,
  redirections,
  galerie,
}: RedirectionsTabProps): JSX.Element {
  const { t } = useTranslation(['regie', 'site', 'commun']);
  const mettreAJour = useMettreAJourRedirections(galerieSlug);
  const peutEcrire = galerieALaction(galerie, 'site:write');

  const [lignes, setLignes] = useState<CorpsRedirection[]>(
    redirections.map((redirection) => ({ de: redirection.de, vers: redirection.vers })),
  );
  const [de, setDe] = useState('');
  const [vers, setVers] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState(false);

  function ajouter(): void {
    if (!de.trim() || !vers.trim()) return;
    setLignes((precedent) => [...precedent, { de: de.trim(), vers: vers.trim() }]);
    setDe('');
    setVers('');
  }

  async function enregistrer(): Promise<void> {
    setErreur(null);
    setSucces(false);
    try {
      await mettreAJour.mutateAsync(lignes);
      setSucces(true);
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.formulaire}>
      {lignes.length === 0 ? (
        <p className={`${styles.tableauCadre} ${styles.vide}`}>
          {t('regie:site.redirections.aucuneRedirection')}
        </p>
      ) : (
        <div className={styles.tableauCadre}>
          <table className={`${styles.tableau} ${styles.tableauFiches}`}>
            <thead>
              <tr>
                <th scope="col">{t('regie:site.redirections.colonneDe')}</th>
                <th scope="col">{t('regie:site.redirections.colonneVers')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne, index) => (
                <tr key={`${ligne.de}-${String(index)}`}>
                  <td
                    className={styles.cellulePleine}
                    data-intitule={t('regie:site.redirections.colonneDe')}
                  >
                    <span className={styles.chemin}>{ligne.de}</span>
                  </td>
                  <td
                    className={styles.cellulePleine}
                    data-intitule={t('regie:site.redirections.colonneVers')}
                  >
                    <span className={styles.chemin}>
                      <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
                      {ligne.vers}
                    </span>
                  </td>
                  <td className={styles.celluleAction}>
                    {peutEcrire ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setLignes((precedent) => precedent.filter((_, i) => i !== index))
                        }
                      >
                        <Trash2 size={14} strokeWidth={1.5} aria-hidden />
                        {t('commun:actions.supprimer')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {peutEcrire ? (
        <div className={styles.ligneAjout}>
          <Input
            aria-label={t('regie:site.redirections.colonneDe')}
            placeholder="/ancienne-adresse"
            mono
            value={de}
            onChange={(event) => setDe(event.target.value)}
          />
          <span className={styles.ligneAjoutFleche} aria-hidden="true">
            <ArrowRight size={16} strokeWidth={1.5} />
          </span>
          <Input
            aria-label={t('regie:site.redirections.colonneVers')}
            placeholder="/fr/nouvelle-adresse/"
            mono
            value={vers}
            onChange={(event) => setVers(event.target.value)}
          />
          <Button variant="secondary" onClick={ajouter} disabled={!de.trim() || !vers.trim()}>
            <Plus size={16} strokeWidth={1.5} aria-hidden />
            {t('regie:site.redirections.ajouter')}
          </Button>
        </div>
      ) : null}

      {erreur || succes || peutEcrire ? (
        <div className={styles.barreActions}>
          <div className={styles.barreMessages}>
            {erreur ? (
              <p className={styles.erreur} role="alert">
                {erreur}
              </p>
            ) : null}
            {succes ? (
              <p className={styles.succes} role="status">
                {t('site:messages.enregistre')}
              </p>
            ) : null}
          </div>
          {peutEcrire ? (
            <Button onClick={() => void enregistrer()} loading={mettreAJour.isPending}>
              {t('commun:actions.enregistrer')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
